import { ReactElement, useCallback, useEffect, useState } from 'react';

// Hook inputs
type DelayedRemountHookProps = {
  content: (args: { isRemounting: boolean; remount: () => void }) => ReactElement;
  delay: number;
};

// Hook states
enum DelayedRemountStates {
  Mounted,
  TimerActivated,
  Unmounted,
}
const isMounted = (state: DelayedRemountStates) => state === DelayedRemountStates.Mounted;
const isTimerActivated = (state: DelayedRemountStates) =>
  state === DelayedRemountStates.TimerActivated;
const isUnmounted = (state: DelayedRemountStates) => state === DelayedRemountStates.Unmounted;

/**
 * Hook: useDelayedRemount
 *
 * Accepts a `content` render function and a delay. Provides `content` render function
 * with a callback to remount itself after a delay. This can be used by an error boundary
 * to re-mount a page after an error without performing a page reload.
 */
export const useDelayedRemount = ({ content, delay }: DelayedRemountHookProps) => {
  const [state, setState] = useState(DelayedRemountStates.Mounted);

  // State transition 1: callback to activate a timer prior to remounting content
  const remount = useCallback(() => {
    setState((prevState) => (isMounted(state) ? DelayedRemountStates.TimerActivated : prevState));
  }, []);

  // State transition 2: effect to unmount the content after a timer has been executed
  useEffect(() => {
    if (isTimerActivated(state)) setTimeout(() => setState(DelayedRemountStates.Unmounted), delay);
  }, [isTimerActivated(state)]);

  // State transition 3: effect to re-mount the content after being unmounted
  useEffect(() => {
    if (isUnmounted(state)) setState(DelayedRemountStates.Mounted);
  }, [isUnmounted(state)]);

  return {
    content: isUnmounted(state) ? (
      <></>
    ) : (
      content({ isRemounting: isTimerActivated(state) || isUnmounted(state), remount })
    ),
  };
};
